import { IoClose } from 'react-icons/io5';
import Joyride, {
  CallBackProps,
  EVENTS,
  STATUS,
  Step,
  TooltipRenderProps,
} from 'react-joyride';
import { useLocalStorage, useMount, useSetState } from 'react-use';

import Button from './Button';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { EnumTeamContext } from '@/types/team';
import { COLORS, cn } from '@/utils';

interface State {
  run: boolean;
  stepIndex: number;
  steps: Step[];
  showCloseButton?: boolean;
}

interface Props {
  contentSteps: Step[];
  context?: EnumTeamContext;
  userId?: number;
  onClose?: () => void;
}

// eslint-disable-next-line react/display-name
export const OnboardingStepper = ({
  contentSteps,
  context,
  userId,
  onClose,
}: Props) => {
  const { isDesktop, isMobile } = useIsDesktop();
  const [, setLocalStorageOnboarding] = useLocalStorage<string | null>(
    `onboarding-${context + '-' + userId}`,
    null
  );

  const [{ run, stepIndex, steps, showCloseButton }, setState] =
    useSetState<State>({
      run: false,
      stepIndex: 0,
      steps: [],
      showCloseButton: true,
    });
  const isMyTeam = context === EnumTeamContext.Team11;

  useMount(() => {
    setState({
      run: true,
      steps: contentSteps,
    });
  });

  const setClassesRide = (index: number) => {
    const teamContext = isMyTeam ? 'myTeam-' : 'buildTeam-';
    setTimeout(() => {
      const spotlightDev = document.getElementsByTagName('body')[0];
      spotlightDev?.removeAttribute('class');
      spotlightDev?.classList.add(`step-${teamContext}${index}`);
    }, 0);
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { index, status, type } = data;

    if (([STATUS.FINISHED] as string[]).includes(status)) {
      // Need to set our running state to false, so we can restart if we click start again.
      finishOnboarding();
    } else if (([EVENTS.TOUR_START] as string[]).includes(type)) {
      setClassesRide(index);
    } else if (
      ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)
    ) {
      const nextStepIndex = index + 1;
      setState({
        run: true,
        stepIndex: nextStepIndex,
      });
      setClassesRide(nextStepIndex);
    }
  };

  const Tooltip = ({
    index,
    size,
    isLastStep,
    step,
    primaryProps,
  }: TooltipRenderProps) => {
    return (
      <>
        <div className="bg-gdt-blue-header text-white rounded-lg px-5 pt-8 pb-4 lg:w-[19.75rem] w-[18.5rem]">
          <div className="text-center">
            {step.title && (
              <h3 className="font-bebas text-2xl font-normal">{step.title}</h3>
            )}
            {step.content && (
              <p
                className={cn('font-thin mt-2 lg:px-6 px-0', {
                  'px-4': stepIndex === 0 && isMobile,
                  'px-1': stepIndex === 1 && isMobile,
                  'lg:px-0': stepIndex === 1 && isDesktop,
                  'lg:px-5': stepIndex === 2 && isDesktop,
                  'p-2': stepIndex === 0 && isMobile && isMyTeam,
                  'p-0': stepIndex === 1 && (isMobile || isDesktop) && isMyTeam,
                  '!px-4': stepIndex === 0 && isDesktop && isMyTeam,
                })}
              >
                {step.content}
              </p>
            )}
          </div>
          <div className="flex justify-between mt-8">
            <p className="font-thin">
              {index + 1}/{size}
            </p>
            <span {...primaryProps}>
              {!isLastStep ? 'Siguiente' : 'Finalizar'}
            </span>
          </div>
        </div>
      </>
    );
  };

  // onboarding finalizado. Guardo localstorage
  const finishOnboarding = () => {
    if (context === EnumTeamContext.Build11) {
      setLocalStorageOnboarding(
        JSON.stringify({ onboardingBuild: `showed-${userId}` })
      );
    } else {
      setLocalStorageOnboarding(
        JSON.stringify({ onboardingTeam: `showed-${userId}` })
      );
    }

    setState({
      run: false,
      steps: [],
      stepIndex: 0,
      showCloseButton: false,
    });

    onClose?.();
  };

  return (
    <div className="onboadingSteps">
      {showCloseButton && (
        <Button
          className="closeButtonOnboarding z-[1000] fixed top-4 right-4"
          type="button"
          variant="text"
          onClick={() => {
            finishOnboarding();
          }}
        >
          <IoClose color="white" size={30} />
        </Button>
      )}
      <Joyride
        continuous
        disableCloseOnEsc
        disableOverlayClose
        disableScrollParentFix
        disableScrolling
        showProgress
        callback={handleJoyrideCallback}
        run={run}
        scrollToFirstStep={false}
        stepIndex={stepIndex}
        steps={steps}
        styles={{
          options: {
            arrowColor: COLORS['gdt-blue'].header,
            overlayColor: 'rgba(0, 0, 0, 0.5)',
            primaryColor: 'transparent',
            textColor: '#FFF',
            width: '18.5rem',
            zIndex: 100,
          },
          tooltipTitle: {
            color: 'red',
          },
        }}
        tooltipComponent={Tooltip}
      />
    </div>
  );
};
